/*
 * drawer-dialog.js
 * ────────────────
 * Shared base class for the two full-screen menu/search drawers
 * (<mobile-menu-drawer>, <mobile-search-drawer>). Wraps a native
 * <dialog> so the platform provides modality, top-layer stacking,
 * focus containment, Escape handling, focus restore and ::backdrop.
 *
 * Scope: menu + search drawers ONLY. It does NOT replace
 * assets/bottom-drawer.js (minicart, wishlist, quick-add, size-guide).
 *
 * Subclass contract:
 *   - set `get surfaceName()` → the registered surface id.
 *   - optionally `get usesMorph()` → true to run a View Transition on
 *     open (<768px, VT-capable, motion allowed) so shared header/drawer
 *     elements morph.
 *   - implement async `onOpen(detail)` / `onClose()` hooks.
 *
 * All visual state is driven by the `data-state` attribute on the host
 * ("open" | "closing") and the dialog's native `[open]`. `data-instant`
 * on the host suppresses transitions for one flip (see critical.css).
 */

/* global HTMLElement */

/**
 * Base class for full-screen drawer dialogs.
 * @abstract
 */
class DrawerDialog extends HTMLElement {
  /** @returns {string} the surface id this drawer registers as. */
  get surfaceName() {
    return this.dataset.surface || this.tagName.toLowerCase();
  }

  /** @returns {boolean} whether opening should run a View Transition morph. */
  get usesMorph() {
    return false;
  }

  /** @returns {boolean} whether to emit search:open/close events. */
  get isSearch() {
    return false;
  }

  /** @returns {boolean} whether the manager emits mobile-menu:toggle. */
  get isMobileMenu() {
    return false;
  }

  connectedCallback() {
    if (this.dataset.initialised === 'true') return;

    /** @type {HTMLDialogElement|null} */
    this.dialog = this.querySelector('dialog');
    if (!this.dialog || !window.themeUtils || !window.themeUtils.surfaces) return;

    /* Register with the single-writer surface manager. */
    this._unregister = window.themeUtils.surfaces.register(this.surfaceName, {
      open: (detail, opts) => this.open(detail, opts),
      close: (opts) => this.close(opts),
      openInstant: () => this.openInstant(),
      closeInstant: () => this.closeInstant(),
      afterOpen: (detail) => this.afterOpen(detail),
      exclusive: true,
      morph: this.usesMorph,
      search: this.isSearch,
      mobileMenu: this.isMobileMenu,
    });

    /* Escape → let the manager drive the close so shared state stays true. */
    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      window.themeUtils.surfaces.close(this.surfaceName);
    });

    /* Click on the dialog element itself (backdrop area) closes. */
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) {
        window.themeUtils.surfaces.close(this.surfaceName);
      }
    });

    /* Safety net: if the dialog closes by any native path we didn't
       drive, reconcile shared state. `_managed` guards re-entrancy. */
    this.dialog.addEventListener('close', () => {
      if (this._managed) return;
      delete this.dataset.state;
      delete this.dataset.instant;
      window.themeUtils.unlockScroll(this.surfaceName);
      window.themeUtils.surfaces.close(this.surfaceName);
    });

    this.dataset.initialised = 'true';
  }

  disconnectedCallback() {
    if (this._unregister) this._unregister();
    /* Theme-editor-safe by construction: a re-rendered section leaves
       no orphaned open dialog or lingering scroll lock. */
    if (this.dialog && this.dialog.open) {
      this._managed = true;
      this.dialog.close();
      window.themeUtils.unlockScroll(this.surfaceName);
    }
    this.dataset.initialised = '';
  }

  /**
   * Synchronous visual flip to open. No async hooks — used both by the
   * animated `open()` and by the manager's `replace()` View Transition.
   * @param {boolean} [instant=false] - suppress transitions for this flip.
   */
  #applyOpen(instant = false) {
    this._managed = true;
    if (instant) this.dataset.instant = 'true';
    this.dialog.showModal();
    window.themeUtils.lockScroll(this.surfaceName);
    this.dataset.state = 'open';
    this._managed = false;
    if (instant) requestAnimationFrame(() => delete this.dataset.instant);
  }

  /**
   * Synchronous visual flip to closed.
   * @param {boolean} [instant=false] - suppress the exit transition.
   */
  #applyClose(instant = false) {
    this._managed = true;
    if (instant) this.dataset.instant = 'true';
    this.dialog.close();
    delete this.dataset.state;
    delete this.dataset.instant;
    window.themeUtils.unlockScroll(this.surfaceName);
    this._managed = false;
  }

  /**
   * Open the drawer. Called only by the surface manager.
   * @param {Object} [detail={}] - passed through to onOpen.
   * @param {{animate?: boolean}} [opts={}] - animate:false ⇒ instant flip.
   * @returns {Promise<void>}
   */
  async open(detail = {}, opts = {}) {
    const instant = opts.animate === false || window.themeUtils.prefersReducedMotion;
    const flip = () => this.#applyOpen(instant);

    if (this.usesMorph && !instant && document.startViewTransition && window.innerWidth < 768) {
      const vt = document.startViewTransition(flip);
      await vt.updateCallbackDone.catch(() => {});
    } else {
      flip();
    }

    await this.afterOpen(detail);
  }

  /**
   * Close the drawer. Called only by the surface manager.
   * @param {{animate?: boolean}} [opts={}] - animate:false ⇒ instant flip.
   * @returns {Promise<void>}
   */
  async close(opts = {}) {
    const instant = opts.animate === false || window.themeUtils.prefersReducedMotion;

    if (instant) {
      this.#applyClose(true);
    } else {
      this.dataset.state = 'closing';
      await this.#settled();
      this.#applyClose(false);
    }

    await this.onClose?.();
  }

  /** Instant open with no async hook — for the manager's replace() VT. */
  openInstant() {
    this.#applyOpen(true);
  }

  /** Instant close (fires onClose without awaiting) — for replace() VT. */
  closeInstant() {
    this.#applyClose(true);
    this.onClose?.();
  }

  /**
   * Run the subclass onOpen hook. Kept separate from the visual flip so
   * the manager can snapshot a View Transition without waiting on heavy
   * async work (e.g. search rendering).
   * @param {Object} detail
   * @returns {Promise<void>}
   */
  async afterOpen(detail) {
    await this.onOpen?.(detail);
  }

  /**
   * Resolve once all running animations on this element settle, with a
   * hard 500ms guard so a dropped transitionend never strands the close.
   * @returns {Promise<unknown>}
   */
  #settled() {
    const anims = typeof this.getAnimations === 'function' ? this.getAnimations({ subtree: true }) : [];
    const finiteAnims = anims.filter((a) => {
      try {
        const timing = a.effect && typeof a.effect.getTiming === 'function' ? a.effect.getTiming() : null;
        if (timing) {
          return timing.iterations !== Infinity && timing.duration !== Infinity;
        }
      } catch (e) {}
      return true;
    });
    return Promise.race([
      Promise.all(finiteAnims.map((a) => a.finished.catch(() => {}))),
      new Promise((resolve) => setTimeout(resolve, 500)),
    ]);
  }
}

if (!window.customElements.get('drawer-dialog')) {
  /* Not registered as a custom element itself — it is an abstract base.
     Exposed on window so the two subclass snippets can extend it. */
  window.DrawerDialog = DrawerDialog;
}
